import 'client-only';

import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { DATA_KEYS } from '@/consts/dataKeys';
import { isCardSame } from '@/helpers/productSelectionAndCheckout/helpers';
import {
  type SelectableOption,
  type Card,
} from '@/types/productSelectionAndCheckout/types';

const otcCardsAtom = atom<undefined | Card[]>(undefined);

export type SwapOtcCardSelectableOption = ReturnType<
  typeof useOtcCards
>['swapOtcCardSelectableOption'];

export const useOtcCards = () => {
  const { otcCards: initialOtcCards, cards } = useDataProvider(
    DATA_KEYS.productSelection,
  );
  const [otcCards, setOtcCards] = useAtom(otcCardsAtom);

  const currentOtcCards = otcCards ?? initialOtcCards;

  const swapOtcCards = useCallback(
    (oldCard: Card, newCard: Card) => {
      const newOtcCards = [...currentOtcCards];

      const index = newOtcCards.findIndex((card) => isCardSame(oldCard, card));
      if (index !== -1) {
        newOtcCards[index] = newCard;
      }

      setOtcCards(newOtcCards);
    },
    [currentOtcCards, setOtcCards],
  );

  const swapOtcCardSelectableOption = useCallback(
    (selectableOption: SelectableOption) => {
      const newCard = cards.find(
        (card) => card.fullSizeItemId === selectableOption.fullSizeItemId,
      );

      const otcCardForSelectableOption = currentOtcCards.find((card) =>
        card.selectableOptions?.some(
          (cardSelectableOption) =>
            cardSelectableOption.fullSizeItemId ===
            selectableOption.fullSizeItemId,
        ),
      );

      if (
        newCard &&
        otcCardForSelectableOption &&
        !isCardSame(otcCardForSelectableOption, newCard)
      ) {
        swapOtcCards(otcCardForSelectableOption, newCard);
        return {
          oldCard: otcCardForSelectableOption,
          newCard,
        } as const;
      }

      return false;
    },
    [cards, currentOtcCards, swapOtcCards],
  );

  const { recommendedOtcCards, addOnOtcCards } = useMemo(() => {
    const recommendedOtcCards: Card[] = [];
    const addOnOtcCards: Card[] = [];

    currentOtcCards.forEach((otcCard) => {
      if (otcCard.recommended) {
        recommendedOtcCards.push(otcCard);
      } else {
        addOnOtcCards.push(otcCard);
      }
    });

    return {
      recommendedOtcCards,
      addOnOtcCards,
    };
  }, [currentOtcCards]);

  const cardsOfType = (type: 'recommended' | 'addons') =>
    type === 'recommended' ? recommendedOtcCards : addOnOtcCards;

  return {
    otcCards: currentOtcCards,
    setOtcCards,
    swapOtcCards,
    swapOtcCardSelectableOption,
    recommendedOtcCards,
    addOnOtcCards,
    cardsOfType,
  };
};
